import type { LiveCallResponse, LiveCallTone } from '@vagusx/clients';

/**
 * Presentation helpers for the Live Calls page. Pure so the wording and
 * the arithmetic can be tested without rendering anything.
 */

/**
 * A running call clock: `m:ss`, growing to `h:mm:ss` past an hour.
 *
 * Negative input clamps to zero. The caller adds a locally-counted delta
 * to a server reading, so a client clock that is behind the server's can
 * briefly produce a negative — showing "-0:03" would look like a bug in
 * the thing being monitored rather than in the monitor.
 */
export function formatElapsed(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;

  const pad = (n: number): string => String(n).padStart(2, '0');

  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/**
 * The `CallReason` intents the voice runtime reports, in the words a
 * practice uses for them. See `callReasonSchema` in @vagusx/shared.
 */
const REASON_LABELS: Readonly<Record<string, string>> = {
  schedule_referral: 'Referral',
  general_inquiry: 'General',
};

/**
 * What the call is about, or null when nothing was recorded.
 *
 * An intent this build doesn't know is TITLE-CASED rather than dropped: a
 * reason added to the runtime before the web app catches up should still
 * read as something on the row, and a blank chip would look like missing
 * data rather than a new value.
 */
export function reasonLabel(reason: string | null): string | null {
  if (!reason) return null;

  const known = REASON_LABELS[reason];
  if (known) return known;

  return reason
    .split('_')
    .map((word) => (word ? word[0]!.toUpperCase() + word.slice(1) : word))
    .join(' ');
}

/**
 * Who is on the call. Falls back to the direction-appropriate stand-in:
 * an inbound call from a number we can't match to a chart is an unknown
 * caller; an outbound one always has a chart, so a missing name there is
 * a data problem worth naming differently.
 */
export function callerName(call: Pick<LiveCallResponse, 'patient' | 'direction'>): string {
  const name = [call.patient?.firstName, call.patient?.lastName].filter(Boolean).join(' ').trim();
  if (name) return name;

  return call.direction === 'outbound' ? 'Unnamed patient' : 'Unknown caller';
}

/**
 * Inbound, outbound — or neither.
 *
 * `direction` is nullable: the API narrows a stray `interactions.direction`
 * to null rather than casting it, precisely so one bad row costs one row.
 * A two-way `=== 'inbound' ? … : …` throws that away and renders a call we
 * know nothing about as a confident "Outbound" — the one thing a
 * monitoring view must not do. The API's own counts agree: a null-direction
 * call is in `total` and in neither `inbound` nor `outbound`.
 */
export function directionLabel(direction: string | null): 'Inbound' | 'Outbound' | 'Unknown' {
  if (direction === 'inbound') return 'Inbound';
  if (direction === 'outbound') return 'Outbound';
  return 'Unknown';
}

/**
 * What a tone means, in words, for a screen reader.
 *
 * Tone otherwise reaches the user only as a row tint and a coloured clock,
 * so a reader hears an escalated call and a routine one identically. Null
 * for the tones that carry no warning — appending "normal" to every row
 * would be noise.
 */
export function toneAnnouncement(tone: LiveCallTone): string | null {
  if (tone === 'urgent') return 'escalated';
  if (tone === 'attention') return 'needs attention';
  return null;
}

export function callerInitials(call: Pick<LiveCallResponse, 'patient' | 'direction'>): string {
  const first = call.patient?.firstName?.[0] ?? '';
  const last = call.patient?.lastName?.[0] ?? '';
  const initials = `${first}${last}`.toUpperCase();

  return initials || '?';
}

/**
 * Tailwind classes per tone, resolved through theme tokens so a theme
 * override reaches this page without touching it.
 */
export interface ToneStyle {
  /** Row background for a call that wants noticing. */
  row: string;
  /** Text colour for the elapsed clock and the stage line. */
  text: string;
  /** The live dot. */
  dot: string;
}

const TONE_STYLES: Readonly<Record<LiveCallTone, ToneStyle>> = {
  urgent: {
    row: 'bg-destructive/5',
    text: 'text-destructive',
    dot: 'bg-destructive',
  },
  attention: {
    row: 'bg-warning/10',
    // NOT `text-warning-foreground`. That token is the colour meant to sit
    // ON a solid amber fill, and it is near-black in BOTH themes — so on
    // the 10%-amber row above it reads as ordinary text in light mode and
    // is barely legible in dark. `text-warning` itself is too light to
    // pass contrast as small text on white. The tint and the dot carry the
    // tone; the text just needs to be readable and a step bolder than the
    // muted default, which `text-foreground` is in both themes.
    text: 'text-foreground',
    dot: 'bg-warning',
  },
  good: {
    row: '',
    text: 'text-success',
    dot: 'bg-success',
  },
  normal: {
    row: '',
    text: 'text-muted-foreground',
    dot: 'bg-success',
  },
};

export function toneStyle(tone: LiveCallTone): ToneStyle {
  return TONE_STYLES[tone];
}

/**
 * The elapsed time to show right now.
 *
 * `elapsedSeconds` is the server's reading at the moment the response was
 * built; `fetchedAt` is when that response landed. Counting the gap
 * locally keeps the clock moving every second instead of jumping in
 * five-second steps each poll, and — because the baseline comes from the
 * server — a client whose clock is minutes off still shows the right
 * duration rather than the right-looking wrong one.
 */
export function displayedElapsed(elapsedSeconds: number, fetchedAt: number, now: number): number {
  return Math.max(0, elapsedSeconds + Math.floor((now - fetchedAt) / 1000));
}
